import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  UserPlus,
  Check,
  X,
  Clock,
  User,
  Mail,
  Building,
  Loader2,
  AlertCircle,
  Copy,
  ExternalLink,
  CheckCircle,
  RefreshCw,
  Send,
  Award,
  MapPin,
  FileText,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAccessRequests() {
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [activationLinks, setActivationLinks] = useState({});

  useEffect(() => {
    if (isAdmin) {
      fetchRequests();
    }
  }, [isAdmin, activeTab]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? null : activeTab;
      const response = await adminAPI.getAccessRequests(status);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch access requests:', error);
      toast.error('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessingId(requestId);
    try {
      const response = await adminAPI.approveAccessRequest(requestId);
      toast.success('Access request approved! Activation email sent.');
      
      // Store the activation link for display
      if (response.data.activation_link) {
        setActivationLinks(prev => ({
          ...prev,
          [requestId]: response.data.activation_link
        }));
      }
      
      fetchRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to approve request';
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessingId(requestId);
    try {
      await adminAPI.rejectAccessRequest(requestId);
      toast.success('Access request rejected');
      fetchRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reject request';
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleResendActivation = async (request) => {
    // Need to find the user_id from the request
    // For approved requests, we need to look up the user by email
    setProcessingId(request.id);
    try {
      // The user_id should be stored when approval happens
      // We'll use the request ID and email to find the user
      const response = await adminAPI.resendActivation(request.user_id || request.id);
      toast.success(`Activation email resent to ${request.email}`);
      
      // Store the new activation link
      if (response.data.activation_link) {
        setActivationLinks(prev => ({
          ...prev,
          [request.id]: response.data.activation_link
        }));
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend activation email';
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleResetActivation = async (request) => {
    // Reset activation for users who already activated but are having issues
    setProcessingId(request.id);
    try {
      const response = await adminAPI.resetActivation(request.user_id || request.id);
      toast.success(`Activation reset! New link sent to ${request.email}`);
      
      // Store the new activation link
      if (response.data.activation_link) {
        setActivationLinks(prev => ({
          ...prev,
          [request.id]: response.data.activation_link
        }));
      }
      
      fetchRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset activation';
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const copyActivationLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Activation link copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: 'default', className: 'bg-amber-100 text-amber-700 hover:bg-amber-100', icon: Clock },
      approved: { variant: 'default', className: 'bg-blue-100 text-blue-700 hover:bg-blue-100', icon: Check },
      rejected: { variant: 'default', className: 'bg-red-100 text-red-700 hover:bg-red-100', icon: X },
      active: { variant: 'default', className: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100', icon: CheckCircle },
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      parent: 'bg-purple-100 text-purple-700',
      archer: 'bg-green-100 text-green-700',
      coach: 'bg-blue-100 text-blue-700',
      club: 'bg-orange-100 text-orange-700',
    };
    return (
      <Badge variant="outline" className={colors[role] || 'bg-slate-100 text-slate-700'}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600">You need admin privileges to view this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserPlus className="w-7 h-7 text-primary" />
            Access Requests
          </h1>
          <p className="text-slate-600 mt-1">
            Review and manage user access requests
          </p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {pendingCount} pending request{pendingCount !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 max-w-lg">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {activeTab === 'pending' 
                    ? 'No pending access requests' 
                    : `No ${activeTab === 'all' ? '' : activeTab + ' '}requests found`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {requests.map((request) => (
                <Card key={request.id} className="border-slate-200" data-testid={`request-${request.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* User Info */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{request.full_name}</h3>
                              {getStatusBadge(request.status)}
                              {getRoleBadge(request.role)}
                            </div>
                            <div className="flex items-center gap-2 text-slate-600 text-sm">
                              <Mail className="w-4 h-4" />
                              <span>{request.email}</span>
                            </div>
                            {request.organization && (
                              <div className="flex items-center gap-2 text-slate-600 text-sm mt-1">
                                <Building className="w-4 h-4" />
                                <span>{request.organization}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Coach Details Section */}
                        {request.role === 'coach' && (request.coach_member_id || request.coach_certification_level) && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                              <Shield className="w-4 h-4" />
                              Coach Verification Details
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                              {request.coach_member_id && (
                                <div className="flex items-center gap-2 text-amber-700">
                                  <Award className="w-4 h-4 flex-shrink-0" />
                                  <span><strong>Member ID:</strong> {request.coach_member_id}</span>
                                </div>
                              )}
                              {request.coach_certification_level && (
                                <div className="flex items-center gap-2 text-amber-700">
                                  <Award className="w-4 h-4 flex-shrink-0" />
                                  <span><strong>Level:</strong> {request.coach_certification_level}</span>
                                </div>
                              )}
                              {request.coach_city_state && (
                                <div className="flex items-center gap-2 text-amber-700">
                                  <MapPin className="w-4 h-4 flex-shrink-0" />
                                  <span><strong>Location:</strong> {request.coach_city_state}</span>
                                </div>
                              )}
                              {request.coach_certification_file && (
                                <div className="flex items-center gap-2 text-amber-700">
                                  <FileText className="w-4 h-4 flex-shrink-0" />
                                  <a 
                                    href={request.coach_certification_file}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-amber-900"
                                    download
                                  >
                                    View Certification Proof
                                  </a>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-amber-600 mt-2 italic">
                              Please manually verify coach credentials before approving.
                            </p>
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Requested: {new Date(request.requested_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</span>
                          {request.reviewed_at && (
                            <span>Reviewed: {new Date(request.reviewed_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}</span>
                          )}
                        </div>

                        {/* Activation Link Display (for approved requests) */}
                        {(request.status === 'approved' && activationLinks[request.id]) && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Activation Link (share if email fails):
                            </p>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-xs bg-white px-2 py-1 rounded border border-blue-200 truncate">
                                {activationLinks[request.id]}
                              </code>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyActivationLink(activationLinks[request.id])}
                                data-testid={`copy-link-${request.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {request.status === 'pending' && (
                        <div className="flex gap-2 lg:flex-col">
                          <Button
                            onClick={() => handleApprove(request.id)}
                            disabled={processingId === request.id}
                            className="flex-1 lg:w-32"
                            data-testid={`approve-${request.id}`}
                          >
                            {processingId === request.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleReject(request.id)}
                            disabled={processingId === request.id}
                            className="flex-1 lg:w-32 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`reject-${request.id}`}
                          >
                            <X className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      )}
                      
                      {/* Resend Activation for Approved Users */}
                      {request.status === 'approved' && (
                        <div className="flex gap-2 lg:flex-col">
                          <Button
                            onClick={() => handleResendActivation(request)}
                            disabled={processingId === request.id}
                            variant="outline"
                            className="flex-1 lg:w-40 border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid={`resend-${request.id}`}
                          >
                            {processingId === request.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Resend Activation
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Reset Activation for Active Users with issues */}
                      {request.status === 'active' && (
                        <div className="flex gap-2 lg:flex-col">
                          <Button
                            onClick={() => handleResetActivation(request)}
                            disabled={processingId === request.id}
                            variant="outline"
                            className="flex-1 lg:w-40 border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid={`reset-${request.id}`}
                          >
                            {processingId === request.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reset & Resend
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
